import { useEffect, useRef, useState } from 'react';

interface Props {
  value: string;
  onChange: (val: string) => void;
  options: string[];
  onOptionsChange?: (opts: string[]) => void;
  onAdd?: (name: string) => void;
  onRemove?: (name: string) => void;
  placeholder?: string;
  defaultOptions?: string[];
  clearable?: boolean;
  replaceOnChange?: boolean;
  emptyLabel?: string;
}

export default function CreatableSelect({
  value,
  onChange,
  options,
  onOptionsChange,
  onAdd,
  onRemove,
  placeholder = 'Select or type...',
  defaultOptions,
  clearable = false,
  replaceOnChange = false,
  emptyLabel = '—',
}: Props) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [currentOptions, setCurrentOptions] = useState<string[]>(() => {
    const base = [...(defaultOptions ?? [])];
    options.forEach((o) => {
      if (o && !base.includes(o)) base.push(o);
    });
    return base;
  });
  const ref = useRef<HTMLDivElement>(null);

  // Use a content-based key so that async-loaded defaultOptions (e.g. values
  // derived from tape records) are merged into the list once they arrive,
  // without re-triggering on every render when a caller passes a fresh array.
  const defaultKey = (defaultOptions ?? []).join('\u0001');

  useEffect(() => {
    const base: string[] = [];
    [...(defaultOptions ?? []), ...options].forEach((o) => {
      if (o && !base.includes(o)) base.push(o);
    });
    if (replaceOnChange) {
      setCurrentOptions(base);
    } else {
      setCurrentOptions((prev) => {
        const merged = [...prev];
        base.forEach((o) => {
          if (!merged.includes(o)) merged.push(o);
        });
        return merged;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, defaultKey, replaceOnChange]);

  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const filtered = currentOptions.filter(
    (o) => !input || o.toLowerCase().includes(input.toLowerCase())
  );

  const addOption = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (!currentOptions.includes(trimmed)) {
      const next = [...currentOptions, trimmed];
      setCurrentOptions(next);
      onOptionsChange?.(next);
      onAdd?.(trimmed);
    }
    onChange(trimmed);
    setInput('');
    setOpen(false);
  };

  const removeOption = (name: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = currentOptions.filter((o) => o !== name);
    setCurrentOptions(next);
    onOptionsChange?.(next);
    onRemove?.(name);
    if (value === name) {
      const firstDefault = defaultOptions?.[0] ?? '';
      onChange(firstDefault);
    }
  };

  return (
    <div ref={ref} className="relative w-full">
      <div
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 text-sm px-3 py-2 border border-slate-200 rounded-md bg-white cursor-pointer"
      >
        <span className={`flex-1 truncate ${value ? 'text-slate-800' : 'text-slate-400'}`}>
          {value || placeholder}
        </span>
        {clearable && value && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onChange(''); }}
            className="text-slate-400 hover:text-slate-600 cursor-pointer"
          >
            <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
          </button>
        )}
        <i className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-sm z-30 max-h-56 overflow-auto">
          <div className="px-2 py-2 border-b border-slate-100">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (input.trim()) addOption(input);
                }
              }}
              placeholder="Type to filter, Enter to add..."
              className="w-full text-sm px-2 py-1.5 border border-slate-200 rounded focus:outline-none focus:border-slate-900"
              autoFocus
            />
          </div>

          {filtered.length === 0 && input.trim() && (
            <button
              onClick={() => addOption(input)}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer flex items-center gap-2"
            >
              <i className="ri-add-line w-4 h-4 flex items-center justify-center text-slate-400" />
              Add "{input.trim()}"
            </button>
          )}

          {filtered.map((opt) => {
            const isDefault = defaultOptions?.includes(opt) ?? false;
            const isActive = value === opt;
            return (
              <div
                key={opt}
                onClick={() => {
                  onChange(opt);
                  setInput('');
                  setOpen(false);
                }}
                className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer ${isActive ? 'bg-slate-100 text-slate-900' : 'text-slate-700 hover:bg-slate-50'}`}
              >
                <span className="truncate">{opt}</span>
                <div className="flex items-center gap-1 shrink-0 ml-2">
                  {isActive && (
                    <i className="ri-check-line w-4 h-4 flex items-center justify-center text-slate-600" />
                  )}
                  {!isDefault && (
                    <button
                      onClick={(e) => removeOption(opt, e)}
                      title="Remove from list"
                      className="text-slate-400 hover:text-red-500 cursor-pointer"
                    >
                      <i className="ri-close-line w-4 h-4 flex items-center justify-center" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
          <div
            onClick={() => {
              onChange('');
              setInput('');
              setOpen(false);
            }}
            className={`flex items-center justify-between px-3 py-2 text-sm cursor-pointer ${!value ? 'bg-slate-100 text-slate-900' : 'text-slate-400 hover:bg-slate-50'}`}
          >
            <span className="truncate">{emptyLabel}</span>
            {!value && (
              <i className="ri-check-line w-4 h-4 flex items-center justify-center text-slate-600" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}